import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Building,
  ChevronLeft,
  ChevronRight,
  Crosshair,
  Database,
  Globe,
  Headphones,
  MessageSquare,
  Mic,
  Shield,
  ShieldAlert,
  Sparkles,
  Target,
  UserPlus,
  Users,
  Volume2,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';

import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { Card, CardContent } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';
import { DefaultLayout } from '@src/layout';

// 9 AI Voice Agents (1 triage + 8 specialists)
const aiAgents = [
  {
    id: 'greeter',
    name: 'Greeter Agent',
    icon: MessageSquare,
    tools: 4,
    color: 'from-blue-500 to-blue-600',
    tagline: 'Your First Point of Contact',
    description: 'The Greeter Agent serves as the intelligent entry point for all interactions. It understands your intent and seamlessly routes you to the right specialist agent.',
    capabilities: [
      'Natural language understanding',
      'Intent classification',
      'Agent routing',
      'Context handoff',
    ],
    exampleQueries: [
      '"I need help with a compliance issue"',
      '"Can you check our risk posture?"',
      '"I want to run a security scan"',
    ],
  },
  {
    id: 'grc',
    name: 'GRC Agent',
    icon: Shield,
    tools: 50,
    color: 'from-purple-500 to-purple-600',
    tagline: 'Governance, Risk & Compliance Expert',
    description: 'Master of frameworks, controls, and risk management. The GRC Agent handles everything from policy queries to compliance status and risk assessments.',
    capabilities: [
      'Framework compliance tracking',
      'Control management',
      'Risk register updates',
      'Policy documentation',
      'Evidence collection',
      'Audit preparation',
    ],
    exampleQueries: [
      '"What critical risks need attention this week?"',
      '"Show me our SOC 2 compliance status"',
      '"Create a new risk for data breach scenario"',
    ],
  },
  {
    id: 'iam',
    name: 'IAM Agent',
    icon: Users,
    tools: 28,
    color: 'from-amber-500 to-amber-600',
    tagline: 'Identity & Access Management Specialist',
    description: 'Manages the complete identity lifecycle. From access reviews to entitlement management, the IAM Agent ensures the right people have the right access.',
    capabilities: [
      'Access reviews',
      'Entitlement management',
      'Identity lifecycle',
      'Privilege monitoring',
      'Role assignments',
    ],
    exampleQueries: [
      '"Who has admin access to production systems?"',
      '"Show me users with elevated privileges"',
      '"List pending access requests"',
    ],
  },
  {
    id: 'cmdb',
    name: 'CMDB Agent',
    icon: Database,
    tools: 37,
    color: 'from-blue-500 to-cyan-600',
    tagline: 'Asset & Configuration Expert',
    description: 'Your source of truth for all assets. The CMDB Agent tracks hardware, software, and their relationships across your organization.',
    capabilities: [
      'Asset discovery',
      'Configuration tracking',
      'Dependency mapping',
      'Software inventory',
      'Hardware lifecycle',
    ],
    exampleQueries: [
      '"Show me all assets missing security agents"',
      '"What servers are running outdated OS?"',
      '"List all applications in production"',
    ],
  },
  {
    id: 'xdr',
    name: 'XDR Agent',
    icon: ShieldAlert,
    tools: 66,
    color: 'from-red-500 to-red-600',
    tagline: 'Threat Detection & Response',
    description: 'Your security operations copilot. The XDR Agent monitors threats, correlates alerts, and helps you respond to incidents using MITRE ATT&CK.',
    capabilities: [
      'Threat detection',
      'Incident response',
      'Alert correlation',
      'MITRE ATT&CK mapping',
      'IOC management',
    ],
    exampleQueries: [
      '"Are there any active security incidents?"',
      '"Show me alerts from the last 24 hours"',
      '"What IOCs were detected this week?"',
    ],
  },
  {
    id: 'business',
    name: 'Business Agent',
    icon: Building,
    tools: 34,
    color: 'from-indigo-500 to-indigo-600',
    tagline: 'Business Operations & Continuity',
    description: 'Handles organizational operations including BCP, disaster recovery, contracts, and entity management across your business.',
    capabilities: [
      'Business continuity planning',
      'Disaster recovery',
      'Contract management',
      'Entity management',
      'Process documentation',
    ],
    exampleQueries: [
      '"Which contracts expire in the next 90 days?"',
      '"Show me our disaster recovery status"',
      '"List all business entities"',
    ],
  },
  {
    id: 'pentester',
    name: 'PenTester Agent',
    icon: Crosshair,
    tools: 185,
    color: 'from-rose-500 to-rose-600',
    tagline: 'AI-Powered Security Testing',
    description:
      'The most powerful agent in the arsenal. Orchestrates 127 security tools across 76+ Docker containers through an 18-phase automated workflow spanning 42 pre-built scan types — including Nmap, Metasploit, Nuclei, OWASP ZAP, '
      + 'BloodHound, MobSF, PyRIT, Garak, and more.',
    capabilities: [
      'Vulnerability scanning',
      'Penetration testing',
      'Web app security',
      'Network reconnaissance',
      'Exploit validation',
      'Security reporting',
    ],
    exampleQueries: [
      '"Run a vulnerability scan on example.com"',
      '"Check for exposed ports on our network"',
      '"Scan for OWASP Top 10 vulnerabilities"',
    ],
    tools_list: ['Nmap', 'Metasploit', 'Nuclei', 'OWASP ZAP', 'Nikto', 'SQLMap', 'Gobuster', 'Hydra', '+119 more'],
  },
  {
    id: 'support',
    name: 'Support Agent',
    icon: Headphones,
    tools: 29,
    color: 'from-green-500 to-green-600',
    tagline: 'IT Service Management',
    description: 'Your service desk companion. Handles tickets, service requests, and task management to keep IT operations running smoothly.',
    capabilities: [
      'Ticket management',
      'Service requests',
      'Task tracking',
      'SLA monitoring',
      'Escalation handling',
    ],
    exampleQueries: [
      '"Show me open tickets assigned to me"',
      '"Create a new service request"',
      '"What tickets are approaching SLA breach?"',
    ],
  },
  {
    id: 'onboarding',
    name: 'Onboarding Agent',
    icon: UserPlus,
    tools: 12,
    color: 'from-teal-500 to-teal-600',
    tagline: 'Customer Setup & Configuration',
    description: 'Guides new customers through setup. Handles invitations, initial configuration, and ensures a smooth onboarding experience.',
    capabilities: [
      'Customer setup',
      'User invitations',
      'Configuration wizard',
      'Tenant provisioning',
      'Welcome workflows',
    ],
    exampleQueries: [
      '"Set up a new customer tenant"',
      '"Send invitation to new team member"',
      '"Configure initial security settings"',
    ],
  },
];

const totalTools = aiAgents.reduce((sum, agent) => sum + agent.tools, 0);
// Marketing copy rounds down to the nearest ten ('440+'), so it stays true as agents gain tools.
const toolsLabel = `${Math.floor(totalTools / 10) * 10}+`;


function AgentCard({ agent, index }) {
  const IconComponent = agent.icon;

  return (
    <motion.div
      className="h-full"
      viewport={{ once: true }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <div className="h-full bg-slate-800 border border-slate-700 rounded-xl hover:shadow-xl hover:border-indigo-500/50 transition-all duration-300 group overflow-hidden flex flex-col">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start gap-4 mb-4">
            <div className={`w-14 h-14 bg-gradient-to-br ${agent.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
              <IconComponent className="w-7 h-7 text-white" />
            </div>

            <div>
              <h3 className="text-lg font-bold text-white">{agent.name}</h3>
              <p className="text-sm text-slate-400">{agent.tagline}</p>

              <span className="inline-block mt-1 text-xs font-medium bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">
                {agent.tools}
                {' '}
                tools
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="text-slate-400 text-sm mb-4 leading-relaxed">
            {agent.description}
          </p>

          {/* Capabilities */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Capabilities</p>

            <div className="flex flex-wrap gap-1">
              {agent.capabilities.slice(0, 4).map((cap, i) => (
                <span key={i} className="text-xs bg-indigo-900/50 text-indigo-300 px-2 py-0.5 rounded">
                  {cap}
                </span>
              ))}

              {agent.capabilities.length > 4 && (
                <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded">
                  +
                  {agent.capabilities.length - 4}
                  {' '}
                  more
                </span>
              )}
            </div>
          </div>

          {/* Example Queries */}
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Example Queries</p>

            <ul className="space-y-1">
              {agent.exampleQueries.slice(0, 2).map((query, i) => (
                <li key={i} className="text-xs text-slate-400 italic flex items-start">
                  <Mic className="w-3 h-3 text-indigo-400 mr-1.5 mt-0.5 flex-shrink-0" />
                  {query}
                </li>
              ))}
            </ul>
          </div>

          {/* PenTester Tools Badge */}
          {agent.tools_list && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Security Tools</p>

              <div className="flex flex-wrap gap-1">
                {agent.tools_list.map((tool, i) => (
                  <span key={i} className="text-xs bg-rose-900/50 text-rose-300 px-2 py-0.5 rounded">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// Carousel component for agents - shows 3 at a time, auto-advances every 3 seconds
function AgentsCarousel({ agents }) {
  const ITEMS_PER_VIEW = 3;
  const AUTO_SCROLL_INTERVAL = 3000; // 3 seconds
  const totalAgents = agents.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + ITEMS_PER_VIEW) % totalAgents);
  }, [totalAgents]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - ITEMS_PER_VIEW + totalAgents) % totalAgents);
  }, [totalAgents]);

  // Calculate total number of "pages" for dot indicators
  const totalPages = Math.ceil(totalAgents / ITEMS_PER_VIEW);
  const currentPage = Math.floor(currentIndex / ITEMS_PER_VIEW);

  const goToPage = useCallback((pageIndex) => {
    setCurrentIndex(pageIndex * ITEMS_PER_VIEW);
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        nextSlide();
      }, AUTO_SCROLL_INTERVAL);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, nextSlide]);

  // Get current visible agents - always show ITEMS_PER_VIEW agents by wrapping
  const getVisibleAgents = () => {
    const visible = [];
    for (let i = 0; i < ITEMS_PER_VIEW; i++) {
      const index = (currentIndex + i) % totalAgents;
      visible.push(agents[index]);
    }
    return visible;
  };

  const visibleAgents = getVisibleAgents();

  return (
    <section
      className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-indigo-900/50 text-indigo-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-indigo-700/50">
            <Bot className="w-4 h-4 mr-2" />
            9 Specialist Agents
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Your AI Security Team
          </h2>

          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Each agent is a domain expert, trained on security operations best practices and equipped with specialized tools.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            aria-label="Previous agents"
            className={
              'absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-slate-800/80 hover:bg-slate-700 border '
              + 'border-slate-600 rounded-full flex items-center justify-center text-white transition-all hover:scale-110'
            }
            onClick={prevSlide}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            aria-label="Next agents"
            className={
              'absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-slate-800/80 hover:bg-slate-700 border '
              + 'border-slate-600 rounded-full flex items-center justify-center text-white transition-all hover:scale-110'
            }
            onClick={nextSlide}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Cards Container */}
          <div className="overflow-hidden mx-8">
            <motion.div
              key={currentIndex}
              exit={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch"
            >
              {visibleAgents.map((agent, index) => (
                <div className="h-full min-h-[420px]" key={`${currentIndex}-${agent.id}`}>
                  <AgentCard agent={agent} index={index} />
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              aria-label={`Go to page ${index + 1}`}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentPage
                  ? 'bg-indigo-500 w-8'
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              onClick={() => goToPage(index)}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <p className="text-center text-slate-500 text-sm mt-4">
          Viewing agents
          {' '}
          {currentIndex + 1}
          ,
          {' '}
          {((currentIndex + 1) % totalAgents) + 1}
          ,
          {' '}
          {((currentIndex + 2) % totalAgents) + 1}
          {' '}
          of
          {' '}
          {totalAgents}
        </p>
      </div>
    </section>
  );
}

export function AIAgentsPage() {
  const onNavigateToTop = useNavigateToTop();
  // useState lazy initializers are exempt from react-hooks/purity (useMemo's callback isn't).
  const [heroParticles] = useState(() => Array.from({ length: 15 }, () => ({
    left: Math.random() * 100,
    top: Math.random() * 100,
    duration: 3 + Math.random() * 2,
    delay: Math.random() * 2,
  })));
  const [voiceBars] = useState(() => Array.from({ length: 12 }, () => ({
    scaleYPeak: 0.5 + Math.random() * 0.5,
  })));

  return (
    <DefaultLayout>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
          {/* Background decoration */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500 rounded-full blur-3xl opacity-30" />
          </div>

          {/* Animated particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {heroParticles.map((p, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white/30 rounded-full"
                style={{
                  left: `${p.left}%`,
                  top: `${p.top}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: p.duration,
                  repeat: Infinity,
                  delay: p.delay,
                }}
              />
            ))}
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                initial={{ opacity: 0, x: -50 }}
              >
                <div className="inline-flex items-center bg-indigo-500/20 text-indigo-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-indigo-500/30">
                  <Bot className="w-4 h-4 mr-2" />
                  AI-First Security Platform
                </div>

                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                  Meet Your AI Security Team
                </h1>

                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {aiAgents.length}
                  {' '}
                  specialized AI voice agents with
                  {' '}

                  <span className="text-indigo-400 font-semibold">
                    {toolsLabel}
                    {' '}
                    security tools
                  </span>

                  {' '}
                  working
                  together to automate your security operations. Just speak naturally—BOSSS handles the rest.
                </p>

                {/* Voice visualization */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center gap-1 mb-8 h-10"
                >
                  <Mic className="w-6 h-6 text-indigo-400 mr-3" />

                  <div className="flex items-center gap-1 h-10">
                    {voiceBars.map((bar, i) => (
                      <motion.div
                        key={i}
                        style={{ height: 40 }}
                        className="w-1 bg-gradient-to-t from-indigo-500 to-purple-400 rounded-full"
                        animate={{
                          scaleY: [0.2, bar.scaleYPeak, 0.2],
                        }}
                        transition={{
                          duration: 0.8,
                          repeat: Infinity,
                          delay: i * 0.1,
                          ease: 'easeInOut',
                        }}
                      />
                    ))}
                  </div>

                  <Volume2 className="w-6 h-6 text-purple-400 ml-3" />
                </motion.div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Try AI Voice Demo
                  </Button>

                  <Button
                    className="border-2 border-white/30 text-white bg-transparent hover:bg-white/10 px-8 py-3 rounded-lg transition-all"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>

              {/* Stats Grid */}
              <motion.div
                animate={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: 50 }}
                className="grid grid-cols-2 gap-4"
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {[
                  { label: 'AI Voice Agents', value: String(aiAgents.length), icon: Bot, color: 'from-indigo-500 to-purple-600' },
                  { label: 'Security Tools', value: toolsLabel, icon: Zap, color: 'from-amber-500 to-orange-600' },
                  { label: 'Pentest Containers', value: '45+', icon: Target, color: 'from-rose-500 to-red-600' },
                  { label: 'Languages', value: 'Multi', icon: Globe, color: 'from-teal-500 to-cyan-600' },
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    animate={{ opacity: 1, scale: 1 }}
                    initial={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
                  >
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>

                    <p className="text-3xl font-bold text-white">{stat.value}</p>
                    <p className="text-sm text-slate-400">{stat.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Answer-first overview (SEO-82) */}
        <AnswerSection path="/platform/ai-agents" />


        {/* How It Works */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              viewport={{ once: true }}
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Just Ask BOSSS
              </h2>

              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                No complex interfaces. No steep learning curves. Just speak naturally and let our AI agents handle the rest.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: '1', title: 'Speak Your Request', description: 'Ask anything in natural language. "What risks need attention?" or "Run a vulnerability scan on our website."', icon: Mic },
                { step: '2', title: 'AI Routes & Executes', description: 'The Greeter understands your intent and routes to the specialist agent who executes using their tools.', icon: BrainCircuit },
                { step: '3', title: 'Get Instant Results', description: 'Receive answers, reports, or confirmation of completed actions—all in real-time.', icon: Sparkles },
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                >
                  <Card className="h-full text-center border-gray-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                        <item.icon className="w-8 h-8 text-white" />
                      </div>

                      <div className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
                        Step
                        {' '}
                        {item.step}
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* All 9 Agents - Carousel */}
        <AgentsCarousel agents={aiAgents} />

        {/* Integration with Products */}
        <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              viewport={{ once: true }}
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                AI Across Every Module
              </h2>

              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                Every BOSSS product has a dedicated AI agent. Ask questions, automate workflows, and get insights—all through voice.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[
                { name: 'GRC', agent: 'GRC Agent', tools: 50, href: '/platform/grc', color: '#2973a3' },
                { name: 'IAM', agent: 'IAM Agent', tools: 28, href: '/platform/iam', color: '#10b981' },
                { name: 'CMDB', agent: 'CMDB Agent', tools: 37, href: '/platform/cmdb', color: '#3b82f6' },
                { name: 'XDR', agent: 'XDR Agent', tools: 66, href: '/platform/xdr', color: '#ef4444' },
                { name: 'Service Desk', agent: 'Support Agent', tools: 29, href: '/platform/service-desk', color: '#f59e0b' },
                { name: 'Pentest', agent: 'PenTester Agent', tools: 185, href: '/platform/pentest', color: '#f97316' },
                { name: 'Business', agent: 'Business Agent', tools: 34, href: '/platform/business', color: '#6366f1' },
                { name: 'BCRP', agent: 'Business Agent', tools: 34, href: '/platform/resilience', color: '#0d9488' },
                { name: 'Onboarding', agent: 'Onboarding Agent', tools: 12, href: '#', color: '#14b8a6' },
              ].map((product, index) => (
                <motion.div
                  key={product.name}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`bg-slate-800 rounded-xl p-4 border border-slate-700 hover:shadow-lg transition-all ${product.href !== '#' ? 'cursor-pointer hover:border-indigo-500/50' : ''}`}
                  onClick={() => product.href !== '#' && onNavigateToTop(product.href)}
                >
                  <div
                    style={{ backgroundColor: `${product.color}25` }}
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  >
                    <Bot className="w-5 h-5" style={{ color: product.color }} />
                  </div>

                  <h4 className="font-semibold text-white text-sm">{product.name}</h4>
                  <p className="text-xs text-slate-400">{product.agent}</p>

                  <p style={{ color: product.color }} className="text-xs font-medium mt-1">
                    {product.tools}
                    {' '}
                    tools
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* The screenshots that lived here were platform AI capabilities, not
            voice-agent captures -- they now sit on Pentest and Documents beside
            matching copy. Restore this section once the voice console, a live
            transcript or the demo itself has been captured. */}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <Sparkles className="w-16 h-16 text-indigo-400 mx-auto mb-6" />

              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Experience AI-Powered Security Operations
              </h2>

              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Schedule a demo to see how BOSSS AI agents can automate your security workflows,
                answer questions in real-time, and supercharge your team&apos;s productivity.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-lg transition-all hover:scale-105 text-lg"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  <Mic className="w-5 h-5 mr-2" />
                  Try AI Voice Demo
                </Button>

                <Button
                  className="border-2 border-white/30 text-white bg-transparent hover:bg-white/10 px-8 py-4 rounded-lg transition-all text-lg"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Contact Sales
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </DefaultLayout>
  );
}

export default AIAgentsPage;
